// Lien d'ancre RE-cliquable (bug remonté par Marc le 02/08).
//
// Le bug : `<Link to="/#recherche">` posait bien l'URL au premier clic, puis ne faisait plus
// rien. React Router ne re-notifie pas quand la destination est identique à la position
// courante — `useLocation().hash` ne change pas, donc l'effet de scroll de LandingPage
// (`useEffect(..., [hash])`) ne se redéclenche jamais. Le bouton devenait « à usage unique ».
//
// Le correctif : quand on est DÉJÀ sur la page qui porte l'ancre, on ne délègue pas à React
// Router — on scrolle nous-mêmes et on réécrit le hash avec `replaceState`. Deux
// conséquences voulues : le geste est rejouable à l'infini, et il n'empile pas quinze
// entrées d'historique (le bouton Retour continue de ramener à la page précédente, pas au
// clic précédent).
import { Link, useLocation } from 'react-router-dom'
import type { ComponentProps, MouseEvent } from 'react'

type Props = Omit<ComponentProps<typeof Link>, 'to'> & { to: string }

/**
 * Scrolle vers l'élément d'ancre en respectant « animations réduites ».
 *
 * Une ancre portant `data-anchor-top` ramène en HAUT DE PAGE plutôt que sur elle-même.
 * C'est le cas de `#recherche` (Marc 02/08) : la recherche vit dans le hero, et le hero
 * est fait pour être vu ENTIER — s'arrêter sur le champ de recherche coupait le titre et
 * la promesse au-dessus. L'attribut rend l'intention lisible dans le JSX plutôt que de
 * cacher un cas particulier ici.
 */
export function scrollToAnchor(id: string): boolean {
  const cible = document.getElementById(id)
  if (!cible) return false
  const animationsReduites = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  const comportement: ScrollBehavior = animationsReduites ? 'auto' : 'smooth'
  if (cible.hasAttribute('data-anchor-top')) {
    window.scrollTo({ top: 0, left: 0, behavior: comportement })
  } else {
    cible.scrollIntoView({ behavior: comportement, block: 'start' })
  }
  signalerCible(cible, animationsReduites)
  return true
}

// Durée du signal — deux battements. Doit rester synchronisé avec l'animation
// `battement-ancre` d'index.css (0,9 s × 2).
const DUREE_BATTEMENT_MS = 1800

/**
 * Fait « battre » l'élément d'ancre une fois la page arrivée (demande Marc 02/08 : « comme
 * ça on est sûr qu'on ne peut pas le rater »).
 *
 * Le battement est posé APRÈS le scroll et retiré ensuite, sinon un second clic ne
 * rejouerait rien — le navigateur ne redémarre pas une animation déjà en cours sur un
 * élément qui porte déjà la classe. C'est le même piège que le bug d'ancre d'origine.
 *
 * Respecte « animations réduites » : dans ce cas, pas de battement du tout.
 */
function signalerCible(cible: HTMLElement, animationsReduites: boolean): void {
  if (animationsReduites) return
  cible.classList.remove('anchor-battement')
  // Forcer un reflow : sans lui, retirer puis remettre la classe dans le même tick ne
  // relance pas l'animation.
  void cible.offsetWidth
  cible.classList.add('anchor-battement')
  window.setTimeout(() => cible.classList.remove('anchor-battement'), DUREE_BATTEMENT_MS)
}

export default function AnchorLink({ to, onClick, children, ...rest }: Props) {
  const { pathname } = useLocation()
  const [chemin, ancre] = to.split('#')
  const cheminCible = chemin || '/'

  const gererClic = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e)
    if (e.defaultPrevented) return
    // Ctrl/Cmd/Maj + clic, ou clic molette : l'utilisateur veut un nouvel onglet, pas un scroll.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    // Sur une AUTRE page, on laisse React Router naviguer : la page d'arrivée lit le hash
    // au montage et scrolle elle-même. Il n'y a rien à intercepter.
    if (!ancre || pathname !== cheminCible) return

    e.preventDefault()
    if (scrollToAnchor(ancre)) {
      window.history.replaceState(null, '', `${cheminCible}#${ancre}`)
    }
  }

  return (
    <Link to={to} onClick={gererClic} {...rest}>
      {children}
    </Link>
  )
}
